import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { NavigateNext as SeparatorIcon } from '@mui/icons-material';
import { useSelectedWorkspace, useWorkspaceProjects } from '@/store';

/**
 * Breadcrumb navigation component
 * Shows: Workspace > Project > Board/Backlog/Timeline
 */
export const Breadcrumb: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const workspace = useSelectedWorkspace();
  const projects = useWorkspaceProjects();

  const { workspaceSlug, projectKey } = params as { workspaceSlug?: string; projectKey?: string };

  // Only show breadcrumbs on workspace routes
  if (!location.pathname.startsWith('/w/')) return null;

  const project = projectKey
    ? projects.find((p) => p.projectKey === projectKey)
    : null;

  // Determine the current page
  const pathParts = location.pathname.split('/').filter(Boolean);
  let currentPage = '';
  if (pathParts.includes('board')) currentPage = 'Board';
  else if (pathParts.includes('backlog')) currentPage = 'Backlog';
  else if (pathParts.includes('timeline')) currentPage = 'Timeline';
  else if (pathParts.includes('sprints')) currentPage = 'Sprints';
  else if (pathParts.includes('settings')) currentPage = 'Settings';
  else if (pathParts.includes('members')) currentPage = 'Members';
  else if (pathParts.includes('projects') && pathParts.includes('new')) currentPage = 'New Project';
  else if (pathParts.includes('projects')) currentPage = 'Projects';
  else if (pathParts.includes('issues')) currentPage = params.issueKey || 'Issue';

  return (
    <Box sx={{ mb: 2 }}>
      <Breadcrumbs separator={<SeparatorIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.85rem' }}>
        {/* Workspace */}
        {workspace && (
          currentPage || project ? (
            <Link
              component="button"
              variant="body2"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate(`/w/${workspaceSlug}`)}
              sx={{ fontSize: '0.85rem' }}
            >
              {workspace.workspaceName}
            </Link>
          ) : (
            <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
              {workspace.workspaceName}
            </Typography>
          )
        )}

        {/* Project */}
        {project && (
          currentPage ? (
            <Link
              component="button"
              variant="body2"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate(`/w/${workspaceSlug}/p/${projectKey}/board`)}
              sx={{ fontSize: '0.85rem' }}
            >
              {project.groupName}
            </Link>
          ) : (
            <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
              {project.groupName}
            </Typography>
          )
        )}

        {/* Current Page */}
        {currentPage && (
          <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
            {currentPage}
          </Typography>
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default Breadcrumb;
